import { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share2 } from 'lucide-react';

const DISMISS_KEY = 'lifelinke_pwa_install_dismissed';
const DISMISS_DAYS = 7;

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [iosBanner, setIosBanner] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already installed (standalone display)?
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    // Check dismiss state
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = Number(dismissed);
      const daysAgo = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysAgo < DISMISS_DAYS) return;
    }

    // iOS detection (no beforeinstallprompt support, must show manual instructions)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      // Show after 5 seconds delay so user can experience the site
      const timer = setTimeout(() => setIosBanner(true), 5000);
      return () => clearTimeout(timer);
    }

    // Chrome/Edge/Android: capture event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
    setIosBanner(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  if (installed) return null;

  // Android/Chrome/Edge banner
  if (showBanner && deferredPrompt) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 bg-white border-2 border-[#FF3B30] shadow-2xl"
        data-testid="pwa-install-banner"
      >
        <div className="flex items-start gap-3 p-4">
          <div className="w-12 h-12 flex-shrink-0 bg-[#FF3B30] flex items-center justify-center">
            <Smartphone size={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">
              Installa LIFELINKE
            </div>
            <p className="text-[11px] text-[#4A6B8A] mt-1 leading-tight">
              Aggiungi l'app sul tuo dispositivo per accesso rapido alle emergenze, anche offline.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={install}
                className="flex items-center gap-1 px-3 py-2 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF1A1A]"
                data-testid="pwa-install-btn"
              >
                <Download size={11} /> Installa
              </button>
              <button
                onClick={dismiss}
                className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#4A6B8A] hover:text-[#FF3B30]"
                data-testid="pwa-dismiss-btn"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="p-1 text-[#6B8AB5] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
            data-testid="pwa-close-btn"
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // iOS banner (Safari)
  if (iosBanner) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 bg-white border-2 border-[#0A84FF] shadow-2xl"
        data-testid="pwa-ios-banner"
      >
        <div className="flex items-start gap-3 p-4">
          <div className="w-12 h-12 flex-shrink-0 bg-[#0A84FF] flex items-center justify-center">
            <Share2 size={22} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">
              Installa LIFELINKE su iPhone
            </div>
            <p className="text-[11px] text-[#4A6B8A] mt-1 leading-snug">
              Tocca <Share2 size={11} className="inline -mt-0.5 mx-0.5 text-[#0A84FF]" /> <strong>Condividi</strong> in basso, poi scorri e tocca <strong>"Aggiungi a Home"</strong>.
            </p>
            <button
              onClick={dismiss}
              className="mt-3 px-3 py-2 text-[10px] uppercase tracking-widest text-[#4A6B8A] hover:text-[#0A84FF]"
              data-testid="pwa-ios-dismiss-btn"
            >
              Ho capito
            </button>
          </div>
          <button
            onClick={dismiss}
            className="p-1 text-[#6B8AB5] hover:text-[#0A84FF] hover:bg-[#0A84FF]/10"
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
